/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { IColumnWithChild } from "./types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";

const TABLE_HEADER_HEIGHT_CLASS = "h-9";
const TABLE_ROW_HEIGHT_CLASS = "h-[52px]";

interface ICollapsibleTable {
  rowsData: any[];
  columns: IColumnWithChild;
  childHeader?: string | undefined;
  onRowClick?: (row: any) => void;
}
interface IRowProps {
  row: any;
  rowIndex: number;
  columns: IColumnWithChild;
  childHeader?: string | undefined;
  onRowClick?: (row: any) => void;
}
function Row(props: IRowProps) {
  const { row, rowIndex, columns, childHeader, onRowClick } = props;
  const [open, setOpen] = React.useState(false);
  const rowHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(!open);
    !!onRowClick && onRowClick(row);
  };
  return (
    <React.Fragment>
      <TableRow key={`parent-header-${rowIndex}`} className={`cursor-pointer group ${TABLE_ROW_HEIGHT_CLASS}`}>
        <TableCell className="w-10 px-2">
          <button aria-label="expand row" onClick={rowHandler} className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-transparent transition-colors duration-150 hover:bg-accent">
            {open ? <KeyboardArrowUpIcon fontSize="small" className="text-foreground" /> : <KeyboardArrowDownIcon fontSize="small" className="text-foreground" />}
          </button>
        </TableCell>
        {columns.parentColumn.map((item, index: number) => (
          <TableCell key={`${row.index}${index}`} className="px-4 py-0 align-middle">
            {!!item?.renderCell ? item.renderCell(row) : row?.[item.field]}
          </TableCell>
        ))}
      </TableRow>
      {open && (
        <TableRow key={`child-${rowIndex}`} className="align-top">
          {childHeader && (
            <TableCell colSpan={2} className="pb-2 px-4">
              <span className="text-sm font-medium text-foreground">{childHeader}</span>
            </TableCell>
          )}
          <TableCell className="p-0" colSpan={columns.parentColumn.length - (childHeader ? 1 : 0)}>
            <div className="overflow-hidden rounded-md border bg-card m-1">
              <Table className="w-full caption-bottom text-sm" style={{ borderCollapse: 'collapse' }}>
                <TableHeader>
                  <TableRow key={`child-header-${rowIndex}`} className={TABLE_HEADER_HEIGHT_CLASS}>
                    {!!columns?.childColumn &&
                      columns?.childColumn.length > 0 &&
                      columns?.childColumn.map((item: { [key: string]: any }, index: number) => (
                        <TooltipProvider key={`child-header-${rowIndex}-${index}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <TableHead className="px-4 py-0 h-12 whitespace-nowrap align-middle">
                                {item.type !== "actions" && item.headerName}
                              </TableHead>
                            </TooltipTrigger>
                            {item.toolTipTitle && (
                              <TooltipContent>{item.toolTipTitle}</TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      ))}
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr:last-child]:border-border">
                  {!!columns.childKey &&
                    row?.[columns.childKey].map((childRowItem: any, index: number) => (
                      <TableRow key={`child-row-${rowIndex}-${index}`} className={TABLE_ROW_HEIGHT_CLASS}>
                        {!!columns?.childColumn &&
                          columns?.childColumn.map((childCellItem: { [key: string]: any }, cellIndex: number) => (
                            <TableCell key={`child-cell-${rowIndex}-${index}-${cellIndex}`} className="px-4 py-0 align-middle">
                              {!!childCellItem?.renderCell ? childCellItem.renderCell(childRowItem) : childRowItem?.[childCellItem.field]}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

const CollapsibleTable: React.FC<ICollapsibleTable & { style?: React.CSSProperties }> = ({ rowsData, columns, childHeader, onRowClick, style }) => {
  return (
    <div className="w-full h-full overflow-hidden rounded-lg border bg-card text-card-foreground flex flex-col min-h-0" style={style}>
      <Table className="w-full caption-bottom text-sm flex-1 min-h-0" style={{ borderCollapse: 'collapse', height: '100%' }}>
        <TableHeader>
          <TableRow className={TABLE_HEADER_HEIGHT_CLASS}>
            <TableHead className="w-10 px-2"> </TableHead> {/* This is for open and close Arrow icon */}
            {columns.parentColumn.length > 0 &&
              columns.parentColumn.map((item: { [key: string]: any }, index: number) => (
                <TableHead key={index} className="px-4 py-0 whitespace-nowrap align-middle">{item.type !== "actions" && item.headerName}</TableHead>
              ))}
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr:last-child]:border-border">
          {rowsData.map((row, index: number) => (
            <Row key={index} row={row} rowIndex={index} columns={columns} childHeader={childHeader} onRowClick={onRowClick} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CollapsibleTable;
